/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Table, Typography, Button, DatePicker } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, formatIndianCurrency } from "../../../../utils";
import "./style.scss";
import "../style.scss";

const { Title } = Typography;

const ZoneList = ({
    zones: propZones = ["EAST", "NORTH", "SOUTH1", "SOUTH2", "WEST1", "WEST2"],
    zoneData: propZoneData = {},
    businessType: propBusinessType = "",
    reportCategory: propReportCategory = "Business Data",
    displayDate: propDisplayDate = "",
    loading: propLoading = false,
    onZoneClick: propOnZoneClick = null,
    selectedDate: propSelectedDate = null,
    onDateChange: propOnDateChange = null,
    onDateSubmit: propOnDateSubmit = null,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // State for route-based usage
    const [selectedDate, setSelectedDate] = useState(moment());
    const [businessType, setBusinessType] = useState("");
    const [reportCategory, setReportCategory] = useState("Business Data");
    const [reportType, setReportType] = useState("Piece Rate");
    const [dataType, setDataType] = useState("today");
    
    // Determine if component is used as route or as child component
    const isRouteComponent = propOnZoneClick === null;
    
    useEffect(() => {
        if (isRouteComponent) {
            // Get data from location state if used as route
            const category = location.state?.reportCategory || "Business Data";
            const type = location.state?.reportType || "Piece Rate";
            const typeMode = location.state?.type || "today";
            const businessTypeValue = location.state?.businessType || "";
            
            setReportCategory(category);
            setReportType(type);
            setDataType(typeMode);
            setBusinessType(businessTypeValue);
            
            if (location.state?.date) {
                setSelectedDate(moment(location.state.date));
            }
        }
    }, [location.state, isRouteComponent]);
    
    const handleDateSubmit = () => {
        if (!isRouteComponent && propOnDateSubmit) {
            propOnDateSubmit();
        }
    };
    
    
    const handleZoneClick = (zone) => {
        if (zone === "Total") {
            return;
        }
        
        if (isRouteComponent) {
            // Map business type to reportType based on report category
            let reportTypeToPass = reportType;
            
            if (reportCategory === "Business Data") {
                const reportTypeMap = {
                    "Cash": "Cash",
                    "Account": "Account",
                    "DP": "DP",
                    "Piece Rate": "Piece Rate"
                };
                reportTypeToPass = reportTypeMap[businessType] || reportType;
            } else if (reportCategory === "Shipment Counts") {
                const reportTypeMap = {
                    "Cash Count": "Cash",
                    "Account Count": "Account",
                    "DP Counts": "DP",
                    "Piece Rate Count": "Piece Rate"
                };
                reportTypeToPass = reportTypeMap[businessType] || reportType;
            }
            
            // Navigate to hub-wise view
            const routeMap = {
                "Business Data": "/hub-wise-business-data",
                "Shipment Counts": "/hub-wise-shipment-counts",
                "Delivery Data": "/hub-wise-delivery-data",
            };
            navigate(routeMap[reportCategory] || "/hub-wise-business-data", {
                state: {
                    zone: zone,
                    reportType: reportTypeToPass,
                    reportCategory: reportCategory,
                    type: dataType,
                    date: selectedDate.format("YYYY-MM-DD"),
                    businessType: businessType,
                },
            });
        } else if (propOnZoneClick) {
            propOnZoneClick(zone);
        }
    };
    
    // Use props if provided (child component), otherwise use state (route component)
    const finalZones = propZones;
    const finalZoneData = propZoneData;
    const finalBusinessType = isRouteComponent ? businessType : propBusinessType;
    const finalReportCategory = isRouteComponent ? reportCategory : propReportCategory;
    const finalSelectedDate = isRouteComponent ? selectedDate : (propSelectedDate || moment());
    const finalLoading = propLoading;
    
    const todayDate = finalSelectedDate.format("DD/MM/YYYY");
    const rangeStartDate = finalSelectedDate.startOf("month").format("DD/MM/YYYY");
    const rangeEndDate = finalSelectedDate.format("DD/MM/YYYY");
    const dateRange = `${rangeStartDate} - ${rangeEndDate}`;
    const finalDisplayDate = isRouteComponent 
        ? (dataType === "today" ? todayDate : dateRange)
        : propDisplayDate;
    // Format value based on report category
    const formatValue = (value) => {
        const numValue = typeof value === "number" ? value : parseFloat(value) || 0;
        
        if (finalReportCategory === "Business Data") {
            return formatIndianCurrency(numValue);
        } else if (finalReportCategory === "Delivery Data" && finalBusinessType === "Delivery Percentage") {
            return `${numValue.toFixed(2)}%`;
        } else {
            return formatIndianNumber(numValue);
        }
    };
    
    // Calculate total
    const calculateTotal = () => {
        return finalZones.reduce((sum, zone) => {
            const value = finalZoneData[zone] || 0;
            return sum + (typeof value === "number" ? value : parseFloat(value) || 0);
        }, 0);
    };
    
    // Prepare table data
    const tableData = [
        ...finalZones.map((zone) => ({
            key: zone.toLowerCase(),
            zone: zone,
            total: formatValue(finalZoneData[zone] || 0),
        })),
        {
            key: "total",
            zone: "Total",
            total: formatValue(calculateTotal()),
        },
    ];
    
    // Table columns
    const columns = [
        {
            title: "ZONE",
            dataIndex: "zone",
            key: "zone",
            width: "50%",
            render: (text, record) => {
                if (record.zone === "Total") {
                    return <strong>{text}</strong>;
                }
                return (
                    <span
                        onClick={() => handleZoneClick(text)}
                        style={{
                            cursor: "pointer",
                            color: "#1890ff",
                            textDecoration: "none",
                        }}
                    >
                        {text}
                    </span>
                );
            },
        },
        {
            title: "Total",
            dataIndex: "total",
            key: "total",
            width: "50%",
            align: "right",
            render: (value) => <strong>{value || ""}</strong>,
        },
    ];
    const handleGoBack = () => {
        navigate("/director-dashboardNew");
    };
    return (
        <div className="director-dashboard zone-wise-page">
            {/* Breadcrumb */}
            <div className="zone-wise-breadcrumb">
            <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleGoBack}
                    className="back-button"
                >
                    Dashboard
                </Button>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">Business Statistics Report</span>
            </div>

            {/* Date Filter */}
            <Card className="zone-wise-filter-card">
                <div className="zone-wise-date-filter">
                    <label className="zone-wise-date-label">Date:</label>
                    <DatePicker
                        format="DD/MM/YYYY"
                        value={finalSelectedDate}
                        onChange={(date) => {
                            if (isRouteComponent) {
                                setSelectedDate(date || moment());
                            } else if (propOnDateChange) {
                                propOnDateChange(date || moment());
                            }
                        }}
                        className="zone-wise-date-picker"
                    />
                    <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn">
                        Submit
                    </Button>
                </div>
            </Card>

            {/* Zone List Table */}
            <Card className="zone-list-table-card">
                <Title level={4} className="zone-list-table-title">
                    Zone Wise {finalReportCategory} - {finalDisplayDate} {finalBusinessType ? `(${finalBusinessType})` : ""}
                </Title>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    loading={finalLoading}
                    size="small"
                    className="zone-list-table"
                    rowClassName={(record) => {
                        if (record.zone === "Total") {
                            return "zone-list-total-row";
                        }
                        return "";
                    }}
                />
            </Card>
        </div>
    );
};

export default ZoneList;

